import React, { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMarketing } from '../state/MarketingContext';
import { useAuth } from '../state/auth';
import { BannerZone } from '../domain/types';

export const RotatingBannerZone = memo(({ zone, className }: { zone: BannerZone, className?: string }) => {
    const { getBannersByZone } = useMarketing();
    const { user } = useAuth();
    const navigate = useNavigate();
    const banners = getBannersByZone(zone, user);
    const [current, setCurrent] = useState(0);
    const DURATION = 5000;

    useEffect(() => {
        if (!banners || banners.length <= 1) return;
        const interval = setInterval(() => setCurrent(c => (c + 1) % (banners?.length || 1)), DURATION);
        return () => clearInterval(interval);
    }, [banners.length, DURATION]);

    if (banners.length === 0) return null;

    const handleBannerClick = (e: React.MouseEvent) => {
        const isAction = (e.target as HTMLElement).closest('.slide-action');
        if (isAction) {
            navigate(banners[current].linkTo);
        } else {
            setCurrent((prev) => (prev + 1) % banners.length);
        }
    };

    return (
        <div 
            onClick={handleBannerClick}
            className={`w-full flex flex-col items-stretch justify-start rounded-3xl overflow-hidden shadow-lg shadow-black/5 dark:shadow-black/20 bg-surface-light dark:bg-surface-dark relative group h-40 sm:h-56 cursor-pointer ${className || ''}`}
        >
            {banners.map((b, i) => (
                <div key={b.id} className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${i === current ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
                    <div 
                        className="w-full h-full bg-center bg-no-repeat bg-cover absolute inset-0 transition-transform duration-[5000ms] ease-out scale-100 group-hover:scale-105" 
                        style={{backgroundImage: `url("${b.imageUrl}")`}}
                    ></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex items-end p-5">
                        <div className="flex flex-col text-white animate-slide-up">
                            <span className="font-bold text-xl mb-1 leading-tight drop-shadow-md">{b.title}</span>
                            {b.subtitle && (
                                <span className="text-white/90 text-[10px] font-bold uppercase tracking-wider bg-white/20 backdrop-blur-md self-start px-3 py-1 rounded-full border border-white/10 slide-action hover:bg-white/30 transition-colors">
                                    {b.subtitle}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            ))}
            
            {/* Progress Bar */}
            {banners.length > 1 && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 z-20">
                    <div 
                        key={current} 
                        className="h-full bg-primary animate-progress" 
                        style={{ animationDuration: `${DURATION}ms` }}
                    ></div>
                </div>
            )}

            {/* Indicators */}
            {banners.length > 1 && (
                <div className="absolute bottom-4 right-4 z-20 flex gap-2">
                    {banners.map((_, i) => (
                        <button 
                            key={i} 
                            onClick={(e) => { e.stopPropagation(); setCurrent(i); }} 
                            className={`h-1.5 rounded-full transition-all duration-500 ${i === current ? 'bg-white w-6' : 'bg-white/40 w-2'}`} 
                            aria-label={`Slide ${i}`}
                        ></button>
                    ))}
                </div>
            )}
        </div>
    );
});
RotatingBannerZone.displayName = 'RotatingBannerZone';